import useLoggedInUser from '@/composables/logged-in-user/logged-in-user';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useRepo from '@/nebula/repo';
import { DateTime } from 'luxon';
import { EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { RESULT } from 'shared/api/query/constants';
import { accessPolicyType, notificationType } from 'shared/constants.json';
import { computed, ref } from 'vue';
import { defaultRRule, lastOccurrenceBefore } from '@/lib/rrule';
import { makeStaticUserUserScope, makeUserScopeTree } from '@/lib/user-scope-tree';
import { notification as notificationConfig } from 'shared/api/query/configs.json';
import { notificationList } from '@/api/query/nebula/notification';

export default function useUpdateSchedules() {
  const repo = useRepo(notificationConfig.model);

  const { loggedInUser } = useLoggedInUser();
  const { loggedInUserAccount } = useLoggedInUserAccount();

  const createSchedule = () => repo.createSingle(
    {
      creator: { uid: loggedInUser.value.uid },
      title: '',
      icon: '',
      typ: notificationType.writeUpdate,
      schedule: defaultRRule(),
      recipients: makeUserScopeTree({ uid: loggedInUserAccount.value.uid }, makeStaticUserUserScope({ uid: loggedInUser.value.uid })),
      accessPolicy: {
        account: { uid: loggedInUserAccount.value.uid },
        accountAccess: accessPolicyType.disabled,
      },
    },
  ).then((data) => {
    EventBus.$emit(EVENTS.CHECKIN.SCHEDULE_CREATED);
    return data;
  });
  const rangeStartOf = (schedules, at) => {
    const starts = schedules.map((s) => lastOccurrenceBefore(s, at)).filter((s) => s !== null);
    if (starts.length === 0) {
      return '';
    }

    return starts.reduce((a, b) => (a < b ? a : b)).toISO({ suppressMilliseconds: true });
  };

  const schedulesLoading = ref(false);
  const fetchSchedules = () => {
    const schedulesAvailable = repo.entityList.value.some((e) => e.schedule !== undefined);

    schedulesLoading.value = true;
    const now = DateTime.local();
    return repo.query(
      notificationList({
        type: notificationType.writeUpdate,
        timeSeries: [now.toISO()],
        rangeStart: rangeStartOf(repo.entityList.value.map((e) => e.schedule), now),
      }),
    )
      .then((data) => {
        const scheduleIDs = data[RESULT].map((e) => e.uid);
        if (!schedulesAvailable && scheduleIDs.length > 0) {
          return fetchSchedules();
        }

        return repo.selectMultiple(scheduleIDs);
      })
      .finally(() => {
        schedulesLoading.value = false;
      });
  };

  const deleteSchedule = (notification) => repo.deleteSingle(
    notification.uid,
  ).then(() => {
    EventBus.$emit(EVENTS.CHECKIN.SCHEDULE_DELETED);
  });

  const activate = (schedule) => updateSchedule({ uid: schedule.uid, activatedAt: DateTime.local().toISO() });
  const pause = (schedule) => updateSchedule({ uid: schedule.uid, activatedAt: null });

  const updateLoading = ref(false);
  const updateSchedule = (schedule, attributes) => {
    updateLoading.value = true;
    return repo.updateSingle(
      schedule,
      { optimistic: false, attributes },
    ).finally(() => {
      updateLoading.value = false;
    });
  };

  const updateScheduleAccessPolicy = (accessPolicy, notification) => repo.updateSingle(
    { uid: notification.uid, accessPolicy },
    { optimistic: false },
  )
    .then((data) => repo.selectSingle(data.uid))
    .finally(() => {
      updateLoading.value = false;
    });

  return {
    fetchSchedules,
    schedulesLoading,
    schedules: computed(() => repo.entityList.value.filter((s) => s.typ === notificationType.writeUpdate)),
    createSchedule,
    createLoading: repo.createLoading,
    selectSingle: repo.selectSingle,

    updateSchedule,
    updateSingle: repo.updateSingle,
    updateScheduleAccessPolicy,
    pause,
    activate,
    updateLoading,
    deleteLoading: repo.deleteLoading,
    deleteSchedule,
  };
}
